import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import _ from 'lodash';
import { IconButton, Paper, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { MdSend } from 'react-icons/md';

import API from 'utils/api';
import { useSources } from 'hooks';
import { SectionTitle, Tooltip } from 'components';
import { classes } from 'utils';
import PlayScore from './PlayScore';
import PlayText from './PlayText';
import GamePlaySettings from './GamePlaySettings';

import styles from './GamePlay.module.scss';

const playTypeMap = { text: PlayText, score: PlayScore };

const GamePlay = forwardRef((props, ref) => {
  const game = _.get(props.event, 'game') || {};
  const [data, setData] = useState([]);

  const sources = useSources();
  const theme = useTheme();

  const fetchPlays = useCallback(() => {
    if (game._id) {
      API.getPlays(game._id, sources).then(({ data }) => (data ? setData(data) : null));
    }
  }, [sources, game._id]);

  useImperativeHandle(ref, () => ({ reload: fetchPlays }));

  useEffect(() => {
    fetchPlays();
  }, [fetchPlays]);

  const renderTitle = () => {
    const highlight = { fontWeight: 'bold', color: theme.palette.primary.main };

    return (
      <SectionTitle title="Game Plays">
        Shows <span style={highlight}>real time</span> information about the game, such as
        who scored, who missed etc.
      </SectionTitle>
    );
  };

  const renderPlay = (play, isNew) => {
    const Play = playTypeMap[play.type];

    return Play ? (
      <div key={play._id} className={classes(styles.play, isNew ? styles.new : '')}>
        <div className={styles.clock}>
          <Typography variant="caption" align="center">
            {play.period}
          </Typography>
          <Typography variant="caption" align="center">
            {play.clock}
          </Typography>
        </div>

        <Play game={game} play={play} />

        {props.onMessage ? (
          <div className={styles.control}>
            <IconButton
              onClick={() => props.onMessage('play', _.omit(play, '_id'))}
              size="small"
            >
              <Tooltip title="Send this play to chat" placement="top">
                <MdSend />
              </Tooltip>
            </IconButton>
          </div>
        ) : null}
      </div>
    ) : null;
  };

  const plays = data.map(it => renderPlay(it)).filter(it => !!it);

  return (
    <Paper className={styles.gamePlay}>
      <div className={styles.header}>
        {renderTitle()}
        <GamePlaySettings event={props.event} />
      </div>

      <div className={styles.body}>
        <div className={styles.container}>
          {plays.length ? (
            plays
          ) : (
            <Typography className={styles.empty} variant="body2">
              No important plays were made yet.
            </Typography>
          )}
        </div>
      </div>
    </Paper>
  );
});

export default GamePlay;
